import { Link, Navigate, useParams } from "react-router-dom";
import { Clock } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { useContactData } from "../hooks/useContactData";

export const ResourceDetail = () => {
  const { slug } = useParams();
  const contactData = useContactData();
  const articles = contactData.resourceArticles || [];
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/resources" replace />;
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishedAt,
    author: {
      "@type": "Organization",
      name: contactData.companyName,
    },
    publisher: {
      "@type": "Organization",
      name: contactData.companyName,
      logo: {
        "@type": "ImageObject",
        url: `${contactData.websiteUrl}/logo512.png`,
      },
    },
    mainEntityOfPage: `${contactData.websiteUrl}/resources/${article.slug}`,
    keywords: (article.keywords || []).join(", "),
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        canonicalUrl={`${contactData.websiteUrl}/resources/${article.slug}`}
        ogType="article"
        schema={articleSchema}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Resources", item: "/resources" },
          { name: article.title, item: `/resources/${article.slug}` },
        ]}
      />

      <article className="pt-16 pb-24 px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
        <Reveal delay={0.05} className="space-y-5 mb-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary-50 px-3 py-1 text-[10px] font-black uppercase tracking-wider text-primary-700 dark:bg-primary-950/40 dark:text-primary-400">
              {article.category}
            </span>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-text-sub">
              <Clock className="h-3.5 w-3.5" />
              {article.readMinutes} min read
            </span>
            <span className="text-xs font-bold text-text-sub">
              {article.publishedAt}
            </span>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-foreground tracking-tighter leading-[1.05]">
            {article.title}
          </h1>
          <p className="text-lg text-text-sub font-medium leading-relaxed">
            {article.excerpt}
          </p>
        </Reveal>

        <div className="space-y-6">
          {article.content.map((paragraph, idx) => (
            <Reveal key={idx} delay={0.08 + idx * 0.04}>
              <p className="text-base sm:text-lg text-foreground/90 leading-relaxed font-medium">
                {paragraph}
              </p>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 rounded-[28px] border border-border bg-muted p-8 text-center space-y-4">
          <h2 className="font-heading font-black text-2xl text-foreground tracking-tight">
            Put this into practice
          </h2>
          <p className="text-text-sub font-medium">
            See the workflows live or compare plans for your firm size.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact"
              className="rounded-2xl bg-primary-600 px-6 py-3 font-heading font-black text-white hover:bg-primary-700"
            >
              Book a Demo
            </Link>
            <Link
              to="/how-it-works"
              className="rounded-2xl border border-border bg-surface px-6 py-3 font-heading font-black text-foreground"
            >
              How It Works
            </Link>
          </div>
        </Reveal>
      </article>
    </>
  );
};

export default ResourceDetail;
